/**
 * P9-12 · Bottom-nav tab set + active detection (pure, so it's unit-tested
 * independently of React). The wishlist tab is config-driven — a brand with
 * wishlists disabled simply omits it, with no layout gap.
 */
export type TabKey = "home" | "search" | "wishlist" | "cart" | "account";

export type NavTab = {
  key: TabKey;
  label: string;
  href: string;
  /** Which live count feeds this tab's badge, if any. */
  badge?: "cart" | "wishlist";
};

const ALL_TABS: NavTab[] = [
  { key: "home", label: "Home", href: "/" },
  { key: "search", label: "Search", href: "/search" },
  { key: "wishlist", label: "Wishlist", href: "/wishlist", badge: "wishlist" },
  { key: "cart", label: "Cart", href: "/cart", badge: "cart" },
  { key: "account", label: "Account", href: "/account" },
];

/** The ordered, enabled tab set for a brand. */
export function resolveTabs(opts: { wishlistEnabled: boolean }): NavTab[] {
  return ALL_TABS.filter((t) =>
    t.key === "wishlist" ? opts.wishlistEnabled : true,
  );
}

/** Whether a tab matches the current path. Home is exact; others by prefix. */
export function isTabActive(tab: NavTab, pathname: string): boolean {
  if (tab.href === "/") return pathname === "/";
  return pathname === tab.href || pathname.startsWith(`${tab.href}/`);
}

/**
 * "One fixed bar at a time" (P9-DB02): the nav yields the bottom slot to the
 * sticky ATC on the PDP, the cart-page summary bar, and the P9-13 one-page
 * checkout's sticky pay bar. Hidden on those routes (checkout is also a focused,
 * distraction-free flow where the tab bar shouldn't compete).
 */
export function navHiddenOnRoute(pathname: string): boolean {
  return (
    pathname.startsWith("/products/") ||
    pathname === "/cart" ||
    pathname === "/checkout" ||
    pathname.startsWith("/checkout/")
  );
}
